import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Home as HomeIcon, Zap } from 'lucide-react';
import { grundrisseData, projectData, energieausweisData } from '@/data/mock';
import { renderWithPlaceholders } from '@/components/pages/renderWithPlaceholders';

const GrundrisseView = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1a0d] via-[#1a2e1a] to-[#0d1a0d] px-5 py-8 md:p-16 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C9A961]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 md:mb-20"
        >
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <HomeIcon className="w-6 h-6 text-[#D4AF37]" />
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-light text-white mb-4 md:mb-6 tracking-tight" style={{ fontFamily: "'Cormorant', serif" }}>
            {grundrisseData.title}
          </h1>
          
          <p className="text-xl text-[#D4AF37] mb-8 font-light tracking-wider">
            {grundrisseData.subtitle}
          </p>
          
          <div className="h-px w-32 mx-auto mb-8 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          
          <p className="text-lg md:text-xl text-white/70 max-w-4xl mx-auto font-light leading-relaxed">
            {grundrisseData.description}
          </p>
        </motion.div>

        {/* Grundrisse Grid */}
        <div className="space-y-12 md:space-y-20">
          {grundrisseData.haeuser.map((haus, index) => (
            <motion.div
              key={haus.id}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: index * 0.2 }}
              className="luxury-card luxury-blur rounded-3xl overflow-hidden border border-[#D4AF37]/20"
            >
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                {/* Image - Takes 3 columns */}
                <div className="lg:col-span-3 relative h-[420px] sm:h-[560px] lg:h-[680px] group overflow-hidden">
                  <img
                    src={haus.image}
                    alt={`Green Living – ${haus.name} ${haus.subtitle} · Grundriss, unverbindliche Visualisierung, mit KI bearbeitet (AI MODIFIED)`}
                    className="absolute inset-0 w-full h-full object-contain bg-[#1a2e1a] transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 right-4 z-10 text-[10px] uppercase tracking-widest text-white/70 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
                    Unverbindliche Visualisierung
                  </span>
                  {/* Gold Corner Accents */}
                  <div className="absolute top-0 left-0 w-24 h-24">
                    <div className="absolute top-0 left-0 w-16 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                    <div className="absolute top-0 left-0 h-16 w-px bg-gradient-to-b from-[#D4AF37] to-transparent" />
                  </div>
                  <div className="absolute bottom-0 right-0 w-24 h-24">
                    <div className="absolute bottom-0 right-0 w-16 h-px bg-gradient-to-l from-[#D4AF37] to-transparent" />
                    <div className="absolute bottom-0 right-0 h-16 w-px bg-gradient-to-t from-[#D4AF37] to-transparent" />
                  </div>
                </div>

                {/* Content - Takes 2 columns */}
                <div className="lg:col-span-2 p-6 md:p-10 lg:p-14 flex flex-col justify-center bg-gradient-to-br from-[#1a2e1a]/50 to-[#0d1a0d]/50">
                  <div className="mb-8">
                    <span className="text-8xl font-light text-[#D4AF37]/30" style={{ fontFamily: "'Cormorant', serif" }}>
                      {String(haus.id).padStart(2, '0')}
                    </span>
                  </div>

                  <h2 className="text-5xl font-light text-white mb-3" style={{ fontFamily: "'Cormorant', serif" }}>
                    {haus.name}
                  </h2>
                  
                  <div className="h-px w-16 mb-6 bg-gradient-to-r from-[#D4AF37] to-transparent" />
                  
                  <p className="text-[#F4E4C1] text-lg mb-8 font-light tracking-wide">
                    {haus.subtitle}
                  </p>
                  
                  <p className="text-white/80 text-base leading-relaxed mb-10 font-light">
                    {haus.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-1 gap-4">
                    {haus.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 text-sm text-white/70 group-feature"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                        <span className="font-light tracking-wide">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Hinweis */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-12 md:mt-20 luxury-card luxury-blur p-6 md:p-10 rounded-3xl border border-[#D4AF37]/30 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-6" />
          <p className="text-white/80 font-light leading-relaxed text-lg max-w-3xl mx-auto">
            {grundrisseData.hinweis}
          </p>
        </motion.div>

        {/* A4 · Energieausweis-Block (§ 87 GEG) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          data-testid="energieausweis-block"
          className="mt-12 md:mt-20 luxury-card luxury-blur p-6 md:p-10 rounded-3xl border border-[#D4AF37]/30"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
              <h3 className="text-2xl md:text-3xl font-light text-white leading-tight" style={{ fontFamily: "'Cormorant', serif" }}>
                {energieausweisData.title}
              </h3>
            </div>
            <span className="self-start sm:flex-shrink-0 text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 md:px-4 py-1.5 whitespace-nowrap">
              {energieausweisData.badge}
            </span>
          </div>

          {/* Column headers (per building) — same layout on all breakpoints */}
          <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-x-2 sm:gap-x-6 pb-3 mb-2 border-b border-[#D4AF37]/20">
            <span />
            {energieausweisData.columns.map((col, i) => (
              <span key={i} className="text-[#D4AF37] text-[9px] sm:text-xs uppercase tracking-widest text-right font-light">
                {col}
              </span>
            ))}
          </div>

          {/* Rows */}
          <div className="divide-y divide-[#D4AF37]/10">
            {energieausweisData.rows.map((row, i) => (
              <div key={i} className="py-3 grid grid-cols-[1.3fr_1fr_1fr] gap-x-2 sm:gap-x-6 items-baseline">
                <span className="text-[#F4E4C1]/70 text-xs sm:text-sm font-light">{row.label}</span>
                {row.both ? (
                  <span className="col-span-2 text-white text-xs sm:text-base font-light text-right">
                    {row.both}
                  </span>
                ) : (
                  <>
                    <span className="text-white text-xs sm:text-base font-light text-right">
                      {row.haus12}
                    </span>
                    <span className="text-white text-xs sm:text-base font-light text-right">
                      {row.haus34}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>

          <p className="text-white/45 text-xs font-light leading-relaxed mt-8">
            {energieausweisData.fussnote}
          </p>
        </motion.div>

        {/* Background Image Section */}
        <div className="mt-12 md:mt-20 rounded-3xl overflow-hidden h-[240px] md:h-[400px] relative group">
          <img
            src={projectData.images.garden}
            alt="Green Living – Leben am Wasser, Garten-Ansicht · unverbindliche Visualisierung, mit KI bearbeitet (AI MODIFIED)"
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
          />
          <span className="absolute top-4 right-4 z-10 text-[10px] uppercase tracking-widest text-white/70 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
            Visualisierung
          </span>
          <img
            src="/badges/ai_modified_white.png"
            alt="Mit KI bearbeitet (AI MODIFIED)"
            className="absolute bottom-4 left-4 z-10 h-5 md:h-6 w-auto opacity-95 drop-shadow-md"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1a0d] via-[#0d1a0d]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-white/90 text-3xl md:text-4xl font-light italic" style={{ fontFamily: "'Cormorant', serif" }}>
                „Ein Zuhause im Einklang mit der Natur“
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Grain Texture */}
      <div className="grain-texture absolute inset-0 pointer-events-none" />
    </div>
  );
};

export default GrundrisseView;
